// Iter 66: Vetrina pubblica alla radice del sito. Iter 66b: bilingue IT/EN.
import { Link } from "react-router-dom";
import { GooglePlayBadge } from "@/components/GooglePlayBadge";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LangSwitch } from "@/components/LangSwitch";
import {
  HardHat, Smartphone, Monitor, WifiOff, FileText, BadgeCheck,
  BarChart3, FolderKanban, Send, Check, ArrowRight, Users, Clock3,
  ShieldCheck, Euro, Zap,
} from "lucide-react";

const FEATURE_ICONS = [FileText, WifiOff, BadgeCheck, Send, FolderKanban, BarChart3, Zap];

function PriceCard({ nome, prezzo, claim, items, featured, t }) {
  return (
    <div className={`rounded-2xl p-7 border ${featured ? "bg-slate-900 text-white border-slate-900 shadow-2xl md:scale-105" : "bg-white border-slate-200 shadow-sm"}`}>
      {featured && (
        <div className="inline-block text-[10px] font-mono font-bold uppercase tracking-widest bg-blue-500 text-white rounded-full px-3 py-1 mb-3">
          {t("prezzi.featured")}
        </div>
      )}
      <div className="font-heading font-extrabold text-xl">{nome}</div>
      <div className="mt-2 flex items-end gap-1">
        <span className="font-heading font-extrabold text-5xl">{prezzo}€</span>
        <span className={`mb-1 text-sm ${featured ? "text-slate-300" : "text-slate-500"}`}>{t("prezzi.alMese")}</span>
      </div>
      <div className={`mt-1 text-sm ${featured ? "text-slate-300" : "text-slate-600"}`}>{claim}</div>
      <ul className="mt-5 space-y-2.5">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2 text-sm">
            <Check className={`h-4 w-4 mt-0.5 shrink-0 ${featured ? "text-emerald-400" : "text-emerald-600"}`} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
      <Link
        to="/registrati"
        data-testid={`landing-cta-${nome.toLowerCase()}`}
        className={`mt-6 flex items-center justify-center gap-2 rounded-xl px-5 py-3.5 font-heading font-bold transition-colors ${featured ? "bg-blue-500 hover:bg-blue-400 text-white" : "bg-slate-900 hover:bg-slate-700 text-white"}`}
      >
        {t("hero.cta")} <ArrowRight className="h-4 w-4" />
      </Link>
      <div className={`mt-2 text-center text-xs ${featured ? "text-slate-400" : "text-slate-500"}`}>
        {t("prezzi.senzaCarta")}
      </div>
    </div>
  );
}

export default function LandingPage() {
  const { t, i18n } = useTranslation("landing");

  useEffect(() => {
    document.title = t("docTitle");
  }, [i18n.language, t]);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="landing-page">
      {/* Topbar */}
      <header className="sticky top-0 z-40 bg-slate-950/90 backdrop-blur border-b border-white/10 text-white">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-blue-500 grid place-items-center">
              <HardHat className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="font-heading font-extrabold leading-none">IL-Report</div>
              <div className="text-[10px] font-mono text-slate-400 leading-none mt-0.5">RAPPORTINI DI LAVORO</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm text-slate-300">
            <a href="#funzioni" className="hover:text-white transition-colors">{t("nav.funzioni")}</a>
            <a href="#ovunque" className="hover:text-white transition-colors">{t("nav.ovunque")}</a>
            <a href="#prezzi" className="hover:text-white transition-colors">{t("nav.prezzi")}</a>
            <a href="#faq" className="hover:text-white transition-colors">{t("nav.faq")}</a>
          </nav>
          <div className="flex items-center gap-2">
            <LangSwitch dark />
            <Link to="/login" data-testid="landing-login-btn" className="rounded-lg px-4 py-2 text-sm font-heading font-bold text-white/90 hover:bg-white/10 transition-colors">
              {t("accedi", { ns: "common" })}
            </Link>
            <Link to="/registrati" data-testid="landing-topbar-cta" className="hidden sm:block rounded-lg bg-blue-500 hover:bg-blue-400 transition-colors px-4 py-2 text-sm font-heading font-bold">
              {t("provaGratis", { ns: "common" })}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-blue-600/30 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-28">
          <div className="text-[11px] font-mono font-bold tracking-[0.25em] text-blue-400 uppercase">
            {t("hero.kicker")}
          </div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4 max-w-3xl">
            {t("hero.h1a")} <span className="text-blue-400">{t("hero.h1b")}</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-slate-300 max-w-2xl">{t("hero.sub")}</p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/registrati" data-testid="landing-hero-cta" className="inline-flex items-center gap-2 rounded-xl bg-blue-500 hover:bg-blue-400 transition-colors px-6 py-4 font-heading font-bold text-lg">
              {t("hero.cta")} <ArrowRight className="h-5 w-5" />
            </Link>
            <Link to="/demo" data-testid="landing-demo-cta" className="inline-flex items-center gap-2 rounded-xl border border-white/25 hover:bg-white/10 transition-colors px-6 py-4 font-heading font-bold text-lg">
              {t("hero.demo")}
            </Link>
            <GooglePlayBadge dark className="h-[60px]" />
          </div>
          <div className="mt-4 text-xs text-slate-400">{t("hero.nocard")}</div>
          <div className="mt-10 flex flex-wrap gap-2">
            {t("hero.badges").map((b) => (
              <span key={b} className="text-[10px] font-mono font-bold tracking-widest border border-white/20 rounded px-2.5 py-1.5 text-slate-300">
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Cantiere & Ufficio */}
      <section id="ovunque" className="max-w-6xl mx-auto px-4 py-16 md:py-24">
        <div className="text-[11px] font-mono font-bold tracking-[0.25em] text-blue-600 uppercase">{t("ovunque.kicker")}</div>
        <h2 className="font-heading font-extrabold text-lg mt-2">{t("ovunque.title")}</h2>
        <p className="text-slate-600 mt-2 max-w-2xl text-base">{t("ovunque.sub")}</p>
        <div className="grid md:grid-cols-2 gap-6 mt-8">
          <div className="rounded-2xl border border-slate-200 p-7 bg-slate-50">
            <div className="h-11 w-11 rounded-lg bg-slate-900 text-white grid place-items-center">
              <Smartphone className="h-5 w-5" />
            </div>
            <h3 className="font-heading font-extrabold text-lg mt-4">{t("ovunque.mobileTitle")}</h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              {t("ovunque.mobile").map((r) => (
                <li key={r} className="flex gap-2"><Check className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />{r}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border-2 border-blue-200 p-7 bg-blue-50/60">
            <div className="h-11 w-11 rounded-lg bg-blue-600 text-white grid place-items-center">
              <Monitor className="h-5 w-5" />
            </div>
            <h3 className="font-heading font-extrabold text-lg mt-4">{t("ovunque.pcTitle")}</h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              {t("ovunque.pc").map((r) => (
                <li key={r} className="flex gap-2"><Check className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />{r}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Funzionalità */}
      <section id="funzioni" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-16 md:py-24">
          <div className="text-[11px] font-mono font-bold tracking-[0.25em] text-blue-600 uppercase">{t("funzioni.kicker")}</div>
          <h2 className="font-heading font-extrabold text-lg mt-2">{t("funzioni.title")}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
            {t("funzioni.items").map((f, i) => {
              const Icon = FEATURE_ICONS[i] || FileText;
              return (
                <div key={f.title} className="rounded-2xl bg-white border border-slate-200 p-6 hover:shadow-md transition-shadow">
                  <div className="h-10 w-10 rounded-lg bg-slate-900 text-white grid place-items-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="font-heading font-bold mt-3">{f.title}</div>
                  <p className="text-sm text-slate-600 mt-1.5">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Prezzi */}
      <section id="prezzi" className="max-w-6xl mx-auto px-4 py-16 md:py-24">
        <div className="text-[11px] font-mono font-bold tracking-[0.25em] text-blue-600 uppercase">{t("prezzi.kicker")}</div>
        <h2 className="font-heading font-extrabold text-lg mt-2">{t("prezzi.title")}</h2>
        <p className="text-slate-600 mt-2 max-w-2xl text-base">{t("prezzi.sub")}</p>
        <div className="grid md:grid-cols-2 gap-6 mt-10 max-w-3xl">
          <PriceCard nome="Base" prezzo={29} claim={t("prezzi.baseClaim")} items={t("prezzi.base")} t={t} />
          <PriceCard nome="Pro" prezzo={49} claim={t("prezzi.proClaim")} items={t("prezzi.pro")} featured t={t} />
        </div>
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500">
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" />{t("prezzi.note1")}</span>
          <span className="inline-flex items-center gap-1.5"><Euro className="h-4 w-4" />{t("prezzi.note2")}</span>
          <span className="inline-flex items-center gap-1.5"><Clock3 className="h-4 w-4" />{t("prezzi.note3")}</span>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-16 md:py-24">
          <div className="text-[11px] font-mono font-bold tracking-[0.25em] text-blue-600 uppercase">{t("faq.kicker")}</div>
          <h2 className="font-heading font-extrabold text-lg mt-2">{t("faq.title")}</h2>
          <div className="mt-8 space-y-4">
            {t("faq.items").map((f) => (
              <details key={f.q} className="group rounded-xl bg-white border border-slate-200 p-5">
                <summary className="cursor-pointer list-none flex items-center justify-between gap-3 font-heading font-bold">
                  {f.q}
                  <span className="text-slate-400 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <p className="text-sm text-slate-600 mt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA finale */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-blue-600/25 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-20 text-center">
          <h2 className="font-heading font-extrabold text-lg">{t("cta.title")}</h2>
          <p className="text-slate-300 mt-2 max-w-xl mx-auto text-base">{t("cta.sub")}</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/registrati" data-testid="landing-footer-cta" className="inline-flex items-center gap-2 rounded-xl bg-blue-500 hover:bg-blue-400 transition-colors px-6 py-4 font-heading font-bold text-lg">
              <Users className="h-5 w-5" /> {t("hero.cta")}
            </Link>
            <Link to="/demo" className="inline-flex items-center gap-2 rounded-xl border border-white/25 hover:bg-white/10 transition-colors px-6 py-4 font-heading font-bold text-lg">
              {t("cta.demo")}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-8 flex flex-col md:flex-row items-center justify-between gap-3 text-xs font-mono">
          <div>Made by IL-LAB · Crafted for crews on the move</div>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-white transition-colors">{t("footer.privacy")}</Link>
            <Link to="/login" className="hover:text-white transition-colors">{t("accedi", { ns: "common" })}</Link>
            <Link to="/demo" className="hover:text-white transition-colors">{t("footer.demo")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
